import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

/**
 * Parse notes text into a list of update log entries.
 * Supports lines like:
 *   [2024-01-15 10:30] Condition changed from Good to Damaged
 *   [2024-01-15 10:30 UTC] Some event
 * Or plain text (returned as a single "Note" entry).
 */
function parseNotes(notes) {
    if (!notes) return [];

    // Match timestamped log lines
    const logLineRegex = /^\[([^\]]+)\]\s+(.+)$/;
    const lines = notes.split('\n').map(l => l.trim()).filter(Boolean);
    const entries = [];

    for (const line of lines) {
        const match = line.match(logLineRegex);
        if (match) {
            entries.push({ timestamp: match[1].trim(), text: match[2].trim(), isLog: true });
        } else {
            entries.push({ timestamp: null, text: line, isLog: false });
        }
    }

    return entries;
}

function formatTimestamp(ts) {
    if (!ts) return null;
    try {
        const d = new Date(ts);
        if (isNaN(d)) return ts;
        return d.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return ts;
    }
}

function getDeliveryLabel(status) {
    switch (status?.toLowerCase()) {
        case 'in_warehouse': return 'In Warehouse';
        case 'in_route': return 'In Route';
        case 'delivered': return 'Delivered';
        default: return status || '—';
    }
}

function getDeliveryColor(status) {
    switch (status?.toLowerCase()) {
        case 'in_warehouse': return 'bg-blue-500/15 text-blue-600 border-blue-500/30';
        case 'in_route': return 'bg-orange-500/15 text-orange-600 border-orange-500/30';
        case 'delivered': return 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30';
        default: return 'bg-muted text-muted-foreground border-border';
    }
}

function getConditionColor(status) {
    switch (status?.toLowerCase()) {
        case 'great':
        case 'perfect': return 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30';
        case 'good': return 'bg-blue-500/15 text-blue-600 border-blue-500/30';
        case 'damaged': return 'bg-red-500/15 text-red-600 border-red-500/30';
        default: return 'bg-muted text-muted-foreground border-border';
    }
}

const ItemDetailModal = ({ isOpen, onClose, item }) => {
    const [activeTab, setActiveTab] = useState('photos');
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const touchStartX = useRef(null);

    // Reset state when item changes
    useEffect(() => {
        if (isOpen) {
            setActiveTab('photos');
            setCurrentImageIndex(0);
        }
    }, [isOpen, item?.id]);

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
        };
        if (isOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentImageIndex, item]);

    if (!isOpen || !item) return null;

    const images = item.images || (item.primaryImage ? [{ url: item.primaryImage, alt: item.primaryImageAlt }] : []);
    const notes = parseNotes(item.notes);
    const hasHistory = notes.length > 0;
    const hasMultipleImages = images.length > 1;

    const handlePrev = () =>
        setCurrentImageIndex(i => (i === 0 ? images.length - 1 : i - 1));
    const handleNext = () =>
        setCurrentImageIndex(i => (i === images.length - 1 ? 0 : i + 1));

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) diff > 0 ? handleNext() : handlePrev();
        touchStartX.current = null;
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

            {/* Modal */}
            <div className="relative bg-background w-full sm:max-w-3xl sm:mx-4 sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl border border-border/50"
                style={{ animation: 'slideUp 0.25s ease-out' }}
            >
                {/* Header */}
                <div className="flex items-start justify-between p-5 border-b border-border flex-shrink-0">
                    <div className="flex-1 min-w-0 pr-4">
                        <h2 className="text-xl font-bold text-foreground leading-tight line-clamp-2">{item.name}</h2>
                        <p className="text-xs font-mono text-muted-foreground mt-0.5">LIIN: {item.liin}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                            <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${getConditionColor(item.status)}`}>
                                {item.status}
                            </span>
                            <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${getDeliveryColor(item.deliveryStatus)}`}>
                                {getDeliveryLabel(item.deliveryStatus)}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 w-9 h-9 rounded-full hover:bg-muted transition-colors flex items-center justify-center text-muted-foreground hover:text-foreground"
                        aria-label="Close"
                    >
                        <Icon name="X" size={20} />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-border flex-shrink-0 px-5">
                    <button
                        onClick={() => setActiveTab('photos')}
                        className={`flex items-center gap-2 py-3 px-1 mr-6 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === 'photos'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                            }`}
                    >
                        <Icon name="Images" size={16} />
                        Photos
                        {images.length > 0 && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${activeTab === 'photos' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                                }`}>
                                {images.length}
                            </span>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex items-center gap-2 py-3 px-1 text-sm font-medium border-b-2 transition-colors -mb-px ${activeTab === 'history'
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                            }`}
                    >
                        <Icon name="ClipboardList" size={16} />
                        Updates & Notes
                        {hasHistory && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${activeTab === 'history' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                                }`}>
                                {notes.length}
                            </span>
                        )}
                    </button>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto">

                    {/* ── PHOTOS TAB ── */}
                    {activeTab === 'photos' && (
                        <div>
                            {images.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                                    <Icon name="ImageOff" size={48} />
                                    <p className="mt-3 text-sm">No photos available</p>
                                </div>
                            ) : (
                                <>
                                    {/* Main Image Viewer */}
                                    <div
                                        className="relative bg-black select-none"
                                        onTouchStart={handleTouchStart}
                                        onTouchEnd={handleTouchEnd}
                                    >
                                        <div className="w-full h-72 sm:h-96 flex items-center justify-center overflow-hidden">
                                            <Image
                                                src={images[currentImageIndex]?.url}
                                                alt={images[currentImageIndex]?.alt || item.name}
                                                className="w-full h-full object-contain"
                                            />
                                        </div>

                                        {/* Prev / Next arrows */}
                                        {hasMultipleImages && (
                                            <>
                                                <button
                                                    onClick={handlePrev}
                                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full border border-white/20 flex items-center justify-center text-white transition-colors"
                                                    aria-label="Previous photo"
                                                >
                                                    <Icon name="ChevronLeft" size={20} />
                                                </button>
                                                <button
                                                    onClick={handleNext}
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-full border border-white/20 flex items-center justify-center text-white transition-colors"
                                                    aria-label="Next photo"
                                                >
                                                    <Icon name="ChevronRight" size={20} />
                                                </button>

                                                {/* Counter */}
                                                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full border border-white/20">
                                                    {currentImageIndex + 1} / {images.length}
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Thumbnails */}
                                    {hasMultipleImages && (
                                        <div className="p-4">
                                            <p className="text-xs text-muted-foreground font-medium mb-3 uppercase tracking-wide">All Photos</p>
                                            <div className="grid grid-cols-5 sm:grid-cols-8 gap-2">
                                                {images.map((img, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => setCurrentImageIndex(idx)}
                                                        className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${currentImageIndex === idx
                                                            ? 'border-primary ring-2 ring-primary/30 scale-105'
                                                            : 'border-border hover:border-primary/50 hover:scale-102'
                                                            }`}
                                                        aria-label={`View photo ${idx + 1}`}
                                                    >
                                                        <Image
                                                            src={img.url}
                                                            alt={img.alt || `Photo ${idx + 1}`}
                                                            className="w-full h-full object-cover"
                                                        />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* ── HISTORY TAB ── */}
                    {activeTab === 'history' && (
                        <div className="p-5">
                            {/* Item meta details */}
                            <div className="grid grid-cols-2 gap-3 mb-6 p-4 bg-muted/40 rounded-xl border border-border/50">
                                <div>
                                    <p className="text-xs text-muted-foreground mb-0.5">Arrived</p>
                                    <p className="text-sm font-medium text-foreground">
                                        {item.arrivalDate
                                            ? new Date(item.arrivalDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                            : '—'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground mb-0.5">Delivery Status</p>
                                    <span className={`inline-block text-xs px-2.5 py-0.5 rounded-full border font-medium ${getDeliveryColor(item.deliveryStatus)}`}>
                                        {getDeliveryLabel(item.deliveryStatus)}
                                    </span>
                                </div>
                                {item.category && (
                                    <div>
                                        <p className="text-xs text-muted-foreground mb-0.5">Category</p>
                                        <p className="text-sm font-medium text-foreground capitalize">{item.category}</p>
                                    </div>
                                )}
                                {item.location && (
                                    <div>
                                        <p className="text-xs text-muted-foreground mb-0.5">Location</p>
                                        <p className="text-sm font-medium text-foreground">{item.location}</p>
                                    </div>
                                )}
                                {item.description && (
                                    <div className="col-span-2">
                                        <p className="text-xs text-muted-foreground mb-0.5">Description</p>
                                        <p className="text-sm text-foreground">{item.description}</p>
                                    </div>
                                )}
                            </div>


                            {/* Update timeline */}
                            {!hasHistory ? (
                                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                                    <Icon name="ClipboardList" size={40} />
                                    <p className="mt-3 text-sm">No updates recorded yet</p>
                                </div>
                            ) : (
                                <>
                                    <p className="text-xs text-muted-foreground font-medium mb-4 uppercase tracking-wide">Update History</p>
                                    <ol className="relative space-y-0">
                                        {notes.map((entry, idx) => (
                                            <li key={idx} className="relative pl-8 pb-6">
                                                {/* Timeline line */}
                                                {idx < notes.length - 1 && (
                                                    <span className="absolute left-3 top-5 bottom-0 w-px bg-border" />
                                                )}
                                                {/* Dot */}
                                                <span className={`absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center border-2 ${entry.isLog
                                                    ? 'bg-primary/10 border-primary text-primary'
                                                    : 'bg-muted border-border text-muted-foreground'
                                                    }`}>
                                                    <Icon name={entry.isLog ? 'Activity' : 'FileText'} size={12} />
                                                </span>

                                                <div className={`ml-2 p-3 rounded-lg border ${entry.isLog
                                                    ? 'bg-primary/5 border-primary/20'
                                                    : 'bg-muted/40 border-border/50'
                                                    }`}>
                                                    {entry.timestamp && (
                                                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                                                            <Icon name="Clock" size={11} />
                                                            {formatTimestamp(entry.timestamp)}
                                                        </p>
                                                    )}
                                                    <p className="text-sm text-foreground leading-relaxed">{entry.text}</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 p-4 border-t border-border bg-muted/20">
                    <button
                        onClick={onClose}
                        className="w-full py-2.5 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>

            <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </div>
    );
};

export default ItemDetailModal;
